<script setup>
import { computed } from 'vue'

const props = defineProps({
	modelValue: {
		type: Number,
		default: 1,
	},
	totalRows: {
		type: Number,
		required: true,
	},
	perPage: {
		type: Number,
		default: 10,
	},
	size: {
		type: String,
		default: 'md',
	},
	firstNumber: {
		type: Boolean,
		default: false,
	},
	lastNumber: {
		type: Boolean,
		default: false,
	},
	ariaControls: {
		type: String,
		default: undefined,
	},
})

const emit = defineEmits(['update:modelValue'])

const totalPages = computed(() => Math.max(1, Math.ceil(props.totalRows / props.perPage)))

const pageNumbers = computed(() => {
	const total = totalPages.value
	if (props.firstNumber && props.lastNumber) {
		return Array.from({ length: total }, (_, i) => i + 1)
	}

	const current = props.modelValue
	const pages = new Set([1, total, current, current - 1, current + 1])
	return [...pages]
		.filter((page) => page >= 1 && page <= total)
		.sort((a, b) => a - b)
})

const paginationClass = computed(() => (props.size === 'sm' ? 'pagination-sm' : ''))

function goTo(page) {
	if (page < 1 || page > totalPages.value || page === props.modelValue) return
	emit('update:modelValue', page)
}

function showEllipsisBefore(page, index) {
	if (props.firstNumber && props.lastNumber) return false
	if (index === 0) return false
	return page - pageNumbers.value[index - 1] > 1
}
</script>

<template>
	<nav v-if="totalPages > 1" aria-label="Pagination">
		<ul class="pagination mb-0 flex flex-wrap items-center list-none pl-0" :class="paginationClass">
			<li
				class="page-item li-first p-1"
				:class="{ disabled: modelValue <= 1 }"
			>
				<button
					type="button"
					class="page-link"
					:disabled="modelValue <= 1"
					:aria-controls="ariaControls"
					@click="goTo(1)"
				>
					«
				</button>
			</li>
			<li
				class="page-item li-prev p-1"
				:class="{ disabled: modelValue <= 1 }"
			>
				<button
					type="button"
					class="page-link"
					:disabled="modelValue <= 1"
					:aria-controls="ariaControls"
					@click="goTo(modelValue - 1)"
				>
					‹
				</button>
			</li>

			<template v-for="(page, index) in pageNumbers" :key="page">
				<li
					v-if="showEllipsisBefore(page, index)"
					class="page-item li-page bg-dark-2 p-1 disabled"
					aria-hidden="true"
				>
					<span class="page-link">…</span>
				</li>
				<li
					class="page-item li-page bg-dark-2 p-1"
					:class="{ active: page === modelValue }"
				>
					<button
						type="button"
						class="page-link"
						:aria-controls="ariaControls"
						:aria-current="page === modelValue ? 'page' : undefined"
						@click="goTo(page)"
					>
						{{ page }}
					</button>
				</li>
			</template>

			<li
				class="page-item li-next p-1"
				:class="{ disabled: modelValue >= totalPages }"
			>
				<button
					type="button"
					class="page-link"
					:disabled="modelValue >= totalPages"
					:aria-controls="ariaControls"
					@click="goTo(modelValue + 1)"
				>
					›
				</button>
			</li>
			<li
				class="page-item li-last p-1"
				:class="{ disabled: modelValue >= totalPages }"
			>
				<button
					type="button"
					class="page-link"
					:disabled="modelValue >= totalPages"
					:aria-controls="ariaControls"
					@click="goTo(totalPages)"
				>
					»
				</button>
			</li>
		</ul>
	</nav>
</template>
